import React, { useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Navigate, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { useEventLogger } from '../../lib/eventLogger/EventLoggerProvider';
import { usePageTimeTracking } from '../../lib/pageTimeTracking/usePageTimeTracking';
import { isDqPrefillEnabled } from '../../lib/proctor/isDqPrefillEnabled';
import { clearProfileModified, hasProfileModified } from './AutoApplySetupSessionState';
import { hasPendingProfileEdit, verifyProfileEdit } from './profileEditTracking';
import { useClearReviewBeforeSubmitDrafts } from './review/useClearReviewBeforeSubmitDrafts';

const PROFILE_MODIFIED_RETURN_SOURCES = new Set([
    'profile-preferences-edit',
    'profile-resume-edit',
]);

const REVIEW_BEFORE_SUBMIT_PATH = '/auto-apply-setup/review/review-before-submit';

/**
 * Root component for all /auto-apply-setup/* routes.
 * Handles page view and stayed event logging for the entire flow,
 * including terms and report-issue pages. In the disabled treatment it
 * canonicalizes Review variants to RBS; automation-level routes remain page-managed.
 */
const AutoApplySetupRoot: React.FC = () => {
    const { pathname, search: locationSearch, hash, state } = useLocation();
    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    const eventLogger = useEventLogger();
    const [search] = useSearchParams();
    const { getGroup } = useProctorGroups();
    const isApplyForMeDisabled = getGroup('apply_for_me_sfm_tog')?.value === 0;
    const isBlockedApplyForMePath =
        pathname.startsWith('/auto-apply-setup/review') && pathname !== REVIEW_BEFORE_SUBMIT_PATH;
    const shouldRedirect = isApplyForMeDisabled && isBlockedApplyForMePath;
    const shouldClearDrafts =
        pathname === REVIEW_BEFORE_SUBMIT_PATH &&
        getGroup('rnhp_auto_apply_banner_tst')?.value === 2;
    const [clearReviewBeforeSubmitDrafts] = useClearReviewBeforeSubmitDrafts();
    const clearingDrafts = useRef(false);
    const checkingProfile = useRef(false);
    const [verifiedReturn, setVerifiedReturn] = useState(0);

    let currentPage = pathname;
    if (pathname === '/auto-apply-setup/past-answers') {
        const questionType =
            search.get('questionType') === 'dq' && isDqPrefillEnabled(getGroup) ? 'dq' : 'sq';
        currentPage = `${pathname}?questionType=${questionType}`;
    }

    usePageTimeTracking(pathname, (stayedPage, timeStayedOnPageMs) => {
        eventLogger.logAutoApplySetupPageStayedEvent?.({ stayedPage, timeStayedOnPageMs });
    });

    useEffect(() => {
        eventLogger.logAutoApplySetupPageViewEvent?.({ currentPage });
    }, [currentPage, eventLogger]);

    useEffect(() => {
        // Let route canonicalization finish before replacing its query string.
        if (shouldRedirect) return;
        const fromParam = search.get('from');
        if (fromParam && PROFILE_MODIFIED_RETURN_SOURCES.has(fromParam)) {
            // The return URL identifies the editor, not whether anything was saved.
            // ResumePage needs this marker after its asynchronous query resolves
            // to select the edited Indeed Resume over the previously saved choice.
            const isResumeEditorReturn =
                fromParam === 'profile-resume-edit' &&
                (pathname === '/auto-apply-setup/resume' ||
                    pathname === '/auto-apply-setup/resume/edit');
            if (!isResumeEditorReturn) {
                const remainingSearch = new URLSearchParams(search);
                remainingSearch.delete('from');
                // Consume the editor-return event, preserving history state and other
                // params. A reload must not invalidate drafts created after this edit.
                void navigate(
                    { pathname, search: remainingSearch.toString(), hash },
                    { replace: true, state },
                );
            }
        }

        if (checkingProfile.current) return;
        if (hasPendingProfileEdit()) {
            checkingProfile.current = true;
            void verifyProfileEdit(apolloClient)
                .then(() => {
                    checkingProfile.current = false;
                    // Re-evaluate cleanup at the current route, which may have changed
                    // while the server comparison was pending.
                    setVerifiedReturn((value) => value + 1);
                })
                .catch((err: unknown) => {
                    checkingProfile.current = false;
                    console.error('Failed to verify profile edit:', err);
                });
            return;
        }

        // Only confirmed edits authorize cleanup, never an editor-return URL. Keep
        // the marker on failure so a later visit can retry this best-effort cleanup.
        if (shouldClearDrafts && hasProfileModified() && !clearingDrafts.current) {
            clearingDrafts.current = true;
            void clearReviewBeforeSubmitDrafts()
                .then(clearProfileModified)
                .catch((err: unknown) => {
                    console.error('Failed to clear Review Before Submit drafts:', err);
                })
                .finally(() => {
                    clearingDrafts.current = false;
                });
        }
    }, [
        search,
        pathname,
        hash,
        state,
        navigate,
        shouldRedirect,
        shouldClearDrafts,
        clearReviewBeforeSubmitDrafts,
        apolloClient,
        verifiedReturn,
    ]);

    // Bare Review can retain legacy activation behavior, so normalize it along with the
    // Apply For Me variant. Automation-level routes safely filter their own options.
    if (shouldRedirect) {
        return <Navigate to={`${REVIEW_BEFORE_SUBMIT_PATH}${locationSearch}`} replace />;
    }

    return <Outlet />;
};

export default AutoApplySetupRoot;
